/**
 * CreditsPage — /credits
 * Shows plan widget, subscription tiers, credit packs, and usage history.
 */

import { useEffect, useState } from 'react';
import { useCreditsStore, CREDIT_PACKS, CREDIT_COSTS, SUBSCRIPTION_TIERS, SubscriptionTierKey } from '../store/creditsStore';

const C = {
  bg: '#050510',
  surface: '#0d0d1f',
  surface2: '#13132a',
  border: 'rgba(255,255,255,0.06)',
  orange: '#f97316',
  purple: '#7c6ff7',
};

const TIER_FEATURES: Record<string, string[]> = {
  free:    ['25 credits/month', 'No rollover', '2 pipelines/day'],
  indie:   ['300 credits/month', 'Rollover up to 600', '5 pipelines/day', 'Deep Analysis'],
  builder: ['1,000 credits/month', 'Rollover up to 2,000', '15 pipelines/day', 'Deep Analysis', 'Priority support'],
  studio:  ['4,000 credits/month', 'Rollover up to 8,000', '50 pipelines/day', 'Deep Analysis', 'Priority support', 'Team sharing'],
};

// Friendly labels for credit cost table
const COST_LABELS: Record<string, string> = {
  PIPELINE_PROFILE: 'Pipeline: Profile',
  PIPELINE_IDEA: 'Pipeline: Idea',
  PIPELINE_REALITY: 'Pipeline: Reality Check',
  PIPELINE_POSITIONING: 'Pipeline: Positioning',
  PIPELINE_VALIDATION: 'Pipeline: Validation',
  PIPELINE_MVP: 'MVP Blueprint (post-validation)',
  DEEP_PROFILE: 'Deep: Profile',
  DEEP_IDEA: 'Deep: Idea',
  DEEP_REALITY: 'Deep: Reality Check',
  DEEP_POSITIONING: 'Deep: Positioning',
  DEEP_VALIDATION: 'Deep: Validation',
  DEEP_MVP: 'Deep: MVP Blueprint',
  INTEL_NAME: 'Intel: Name Validation',
  INTEL_KEYWORDS: 'Intel: Keywords',
  INTEL_COMPETITORS: 'Intel: Competitors',
  INTEL_TRENDS: 'Intel: Trends',
  INTEL_SENTIMENT: 'Intel: Sentiment',
  INTEL_RUN_ALL: 'Intel: Run All',
  DEEP_SUMMARY: 'Deep Summary',
  DEEP_FINANCIAL: 'Deep Financial',
  DEEP_BETA_PERSONA: 'Deep Beta Persona',
};

export default function CreditsPage() {
  const {
    credits, totalPurchased, totalSpent, history, subscription, subscriptionStatus,
    fetchBalance, fetchSubscriptionStatus, checkout, subscribeCheckout, cancelSubscription, loaded,
  } = useCreditsStore();

  useEffect(() => { document.title = 'Credits & Plans — StartupScore'; }, []);

  useEffect(() => {
    fetchBalance();
    fetchSubscriptionStatus();
  }, [fetchBalance, fetchSubscriptionStatus]);

  const [toast, setToast] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Check for Stripe return params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setTimeout(() => fetchBalance(), 1500);
    }
    if (params.get('subscription') === 'success') {
      const plan = params.get('plan') || '';
      const tierName = SUBSCRIPTION_TIERS[plan as SubscriptionTierKey]?.name || 'your plan';
      setToast(`Welcome to ${tierName}! Your credits have been added.`);
      setTimeout(() => { fetchBalance(); fetchSubscriptionStatus(); }, 1500);
      setTimeout(() => setToast(''), 5000);
      window.history.replaceState({}, '', '/credits');
    }
  }, [fetchBalance, fetchSubscriptionStatus]);

  const currentPlan = subscription?.plan || 'free';
  const isSubscribed = subscription?.status === 'active' && currentPlan !== 'free';

  const handleSubscribe = async (plan: string) => {
    try {
      await subscribeCheckout(plan);
    } catch {
      // redirects to Stripe
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelSubscription();
      setToast('Subscription will cancel at the end of your billing period.');
      setTimeout(() => setToast(''), 5000);
    } catch {
      setToast('Failed to cancel. Please try again.');
      setTimeout(() => setToast(''), 4000);
    }
    setCancelling(false);
  };

  const handleBuy = async (packId: string) => {
    try {
      await checkout(packId);
    } catch {
      // checkout redirects to Stripe
    }
  };

  if (!loaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(124,111,247,0.3)', borderTopColor: C.purple, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)',
          borderRadius: 12, padding: '12px 20px', color: '#22c55e',
          fontSize: 14, fontWeight: 600, backdropFilter: 'blur(8px)',
          animation: 'fadeUp 0.3s ease',
        }}>
          {toast}
        </div>
      )}

      {/* ── Plan & Credits Hero Widget ── */}
      <div style={{
        background: `linear-gradient(135deg, ${C.surface} 0%, ${C.surface2} 100%)`,
        border: `1px solid ${isSubscribed ? 'rgba(249,115,22,0.3)' : C.border}`,
        borderRadius: 24, padding: '32px 36px', marginBottom: 32,
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Left: Plan info */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{
                background: isSubscribed ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.06)',
                border: `1px solid ${isSubscribed ? 'rgba(249,115,22,0.4)' : C.border}`,
                borderRadius: 10, padding: '4px 14px',
                fontSize: 13, fontWeight: 700,
                color: isSubscribed ? C.orange : '#9ca3af',
              }}>
                {isSubscribed ? SUBSCRIPTION_TIERS[currentPlan as SubscriptionTierKey]?.name || currentPlan : 'Free'} Plan
              </span>
              {isSubscribed && subscription?.status === 'active' && (
                <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 600 }}>Active</span>
              )}
            </div>
            <div style={{ fontSize: 48, fontWeight: 900, color: '#fff', marginBottom: 4 }}>
              <span style={{ color: C.orange }}>⚡</span> {credits}
            </div>
            <p style={{ fontSize: 14, color: '#6b7280' }}>credits available</p>
          </div>

          {/* Right: Stats */}
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{totalPurchased}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Purchased</div>
            </div>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.08)' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{totalSpent}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Used</div>
            </div>
            {subscriptionStatus && (
              <>
                <div style={{ width: 1, background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>
                    {subscriptionStatus.dailyPipelinesUsed}/{subscriptionStatus.dailyPipelineLimit}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Pipelines today</div>
                </div>
              </>
            )}
            {isSubscribed && subscriptionStatus?.rolloverCap && (
              <>
                <div style={{ width: 1, background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>
                    {subscriptionStatus.rolloverCap}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Rollover cap</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Billing end + actions */}
        {isSubscribed && (
          <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {subscriptionStatus?.billingEnd && (
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Renews {new Date(subscriptionStatus.billingEnd).toLocaleDateString()}
              </span>
            )}
            <button
              onClick={handleCancel} disabled={cancelling}
              style={{
                padding: '6px 16px', borderRadius: 8,
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
                color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                opacity: cancelling ? 0.5 : 1,
              }}
            >
              {cancelling ? 'Cancelling...' : 'Cancel subscription'}
            </button>
          </div>
        )}
      </div>

      {/* ── Subscription Plans ── */}
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 6 }}>Choose Your Plan</h2>
      <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>Monthly subscription with automatic credit renewal</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16, marginBottom: 40 }}>
        {(Object.entries(SUBSCRIPTION_TIERS) as [SubscriptionTierKey, typeof SUBSCRIPTION_TIERS[SubscriptionTierKey]][]).map(([key, tier]) => {
          const isCurrent = currentPlan === key && isSubscribed;
          const isPopular = key === 'builder';
          return (
            <div
              key={key}
              style={{
                background: C.surface,
                border: `1px solid ${isCurrent ? 'rgba(34,197,94,0.5)' : isPopular ? 'rgba(249,115,22,0.5)' : C.border}`,
                borderRadius: 20, padding: '24px 20px', position: 'relative',
                textAlign: 'center', transition: 'transform 0.2s, border-color 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {isPopular && !isCurrent && (
                <div style={{
                  position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                  background: C.orange, borderRadius: 12, padding: '3px 14px',
                  fontSize: 10, fontWeight: 800, color: '#fff', letterSpacing: '0.06em',
                  textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  MOST POPULAR
                </div>
              )}
              {isCurrent && (
                <div style={{
                  position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                  background: '#22c55e', borderRadius: 12, padding: '3px 14px',
                  fontSize: 10, fontWeight: 800, color: '#fff', letterSpacing: '0.06em',
                  textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  CURRENT PLAN
                </div>
              )}
              <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 4, marginTop: 4 }}>{tier.name}</h3>
              <div style={{ marginBottom: 12 }}>
                {tier.price === 0 ? (
                  <span style={{ fontSize: 28, fontWeight: 900, color: '#9ca3af' }}>Free</span>
                ) : (
                  <>
                    <span style={{ fontSize: 32, fontWeight: 900, color: C.orange }}>${tier.price}</span>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>/mo</span>
                  </>
                )}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.purple, marginBottom: 12 }}>
                {tier.monthlyCredits.toLocaleString()} credits/mo
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', textAlign: 'left' }}>
                {TIER_FEATURES[key]?.map((feat, i) => (
                  <li key={i} style={{ fontSize: 12, color: '#9ca3af', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#22c55e', fontSize: 10 }}>✓</span>
                    {feat}
                  </li>
                ))}
              </ul>
              {key === 'free' ? (
                <div style={{
                  padding: '10px 0', borderRadius: 12,
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${C.border}`,
                  color: '#6b7280', fontSize: 13, fontWeight: 600,
                  textAlign: 'center',
                }}>
                  {isCurrent || !isSubscribed ? 'Current' : 'Downgrade by cancelling'}
                </div>
              ) : isCurrent ? (
                <div style={{
                  padding: '10px 0', borderRadius: 12,
                  background: 'rgba(34,197,94,0.1)',
                  border: '1px solid rgba(34,197,94,0.3)',
                  color: '#22c55e', fontSize: 13, fontWeight: 600,
                  textAlign: 'center',
                }}>
                  Active
                </div>
              ) : (
                <button
                  onClick={() => handleSubscribe(key)}
                  style={{
                    width: '100%', padding: '10px 0', borderRadius: 12,
                    background: isPopular ? C.orange : 'rgba(255,255,255,0.06)',
                    border: isPopular ? 'none' : `1px solid ${C.border}`,
                    color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!isPopular) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                  onMouseLeave={e => { if (!isPopular) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                >
                  {isSubscribed ? 'Switch Plan' : 'Subscribe'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Credit Top-Up Packs ── */}
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 6 }}>Need a top-up?</h2>
      <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>One-time credit packs — works with any plan</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 48 }}>
        {CREDIT_PACKS.map(pack => (
          <div
            key={pack.id}
            style={{
              background: C.surface,
              border: `1px solid ${pack.bestValue ? 'rgba(249,115,22,0.5)' : C.border}`,
              borderRadius: 20, padding: '24px 20px', position: 'relative',
              textAlign: 'center', transition: 'transform 0.2s, border-color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = pack.bestValue ? C.orange : 'rgba(255,255,255,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = pack.bestValue ? 'rgba(249,115,22,0.5)' : C.border; }}
          >
            {pack.bestValue && (
              <div style={{
                position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                background: C.orange, borderRadius: 12, padding: '3px 14px',
                fontSize: 10, fontWeight: 800, color: '#fff', letterSpacing: '0.06em',
                textTransform: 'uppercase', whiteSpace: 'nowrap',
              }}>
                BEST VALUE
              </div>
            )}
            <div style={{ fontSize: 24, marginBottom: 6 }}>{pack.emoji}</div>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#fff', marginBottom: 2 }}>{pack.label}</h3>
            <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>{pack.tagline}</p>
            <div style={{ fontSize: 28, fontWeight: 900, color: C.orange, marginBottom: 2 }}>
              {pack.credits}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>credits</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>{pack.hint}</div>
            <button
              onClick={() => handleBuy(pack.id)}
              style={{
                width: '100%', padding: '10px 0', borderRadius: 12,
                background: pack.bestValue ? C.orange : 'rgba(255,255,255,0.06)',
                border: pack.bestValue ? 'none' : `1px solid ${C.border}`,
                color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!pack.bestValue) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={e => { if (!pack.bestValue) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >
              ${pack.betaPrice.toFixed(2)}
            </button>
          </div>
        ))}
      </div>

      {/* Credit costs reference */}
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 20 }}>Credit Costs</h2>
      <div className="r-table-wrap" style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 16, overflow: 'hidden', marginBottom: 48,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Action</th>
              <th style={{ textAlign: 'center', padding: '14px 20px', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Credits</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(CREDIT_COSTS).map(([action, cost], i) => (
              <tr key={action} style={{ borderBottom: i < Object.keys(CREDIT_COSTS).length - 1 ? `1px solid ${C.border}` : undefined }}>
                <td style={{ padding: '12px 20px', fontSize: 14, color: '#d1d5db' }}>
                  {COST_LABELS[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).toLowerCase().replace(/^\w/, c => c.toUpperCase())}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 20px', fontSize: 14, fontWeight: 600, color: action.startsWith('DEEP_') ? C.purple : '#fff' }}>
                  {cost} cr
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Usage history */}
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 20 }}>Usage History</h2>
      {history.length === 0 ? (
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 16, padding: '48px 24px', textAlign: 'center',
        }}>
          <p style={{ color: '#6b7280', fontSize: 14 }}>No credit activity yet</p>
        </div>
      ) : (
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 16, overflow: 'hidden',
        }}>
          {history.slice(0, 50).map((entry, i) => (
            <div
              key={i}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 20px',
                borderBottom: i < Math.min(history.length, 50) - 1 ? `1px solid ${C.border}` : undefined,
              }}
            >
              <div>
                <span style={{
                  fontSize: 14, fontWeight: 600,
                  color: entry.amount > 0 ? '#00e676' : '#fff',
                }}>
                  {entry.amount > 0 ? `+${entry.amount}` : entry.amount} cr
                </span>
                <span style={{ fontSize: 13, color: '#6b7280', marginLeft: 12 }}>
                  {entry.note || (entry.pack
                    ? `Purchased ${entry.pack} pack`
                    : `${entry.action.replace(/_/g, ' ')}${entry.deep ? ' (Deep)' : ''}`
                  )}
                </span>
              </div>
              <span style={{ fontSize: 12, color: '#4b5563' }}>
                {new Date(entry.createdAt).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
